"use client";

import { useRouter } from "next/navigation";
import { Eye, MessageSquare } from "lucide-react";
import { ForumPostSummary, timeAgo, formatScore, forumPath } from "./ForumService";
import { VoteControl } from "./VoteControl";
import { AuthorName } from "./KarmaHovercard";
import { CategoryChip, LockedBadge, PinnedBadge } from "./ForumBadges";
import { PostActionsMenu } from "./PostActionsMenu";

// One thread as a ledger line: votes, then the label line, then the title in
// the serif. Rows live inside a bordered list — no card per post.
export function PostCard({ post, hideCategory }: { post: ForumPostSummary; hideCategory?: boolean }) {
  const router = useRouter();
  const open = () => router.push(forumPath(`/t/${post.slug}`));

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => e.key === "Enter" && open()}
      className="group flex cursor-pointer gap-3 px-3 py-3 transition-colors hover:bg-muted/40 focus:outline-none focus-visible:bg-muted/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 sm:px-4"
    >
      <div className="shrink-0 self-start" onClick={(e) => e.stopPropagation()}>
        <VoteControl targetType="post" targetId={post.id} score={post.score} myVote={post.myVote} size="sm" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          {post.isPinned && <PinnedBadge />}
          {post.isLocked && <LockedBadge />}
          {post.isFresh && <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">New on the floor</span>}
          {!hideCategory && (
            <CategoryChip
              name={post.category.name}
              slug={post.category.slug}
              onClick={() => router.push(forumPath(`/c/${post.category.slug}`))}
            />
          )}
          <AuthorName author={post.author} />
          <span className="font-mono tabular-nums">{timeAgo(post.createdAt)}</span>
        </div>

        <h3
          className="forum-serif mt-1 text-[16px] font-medium leading-snug text-foreground transition-colors group-hover:text-primary sm:text-[17px]"
        >
          {post.title}
        </h3>

        {post.bodyPreview && (
          <p className="mt-0.5 line-clamp-2 text-[13px] leading-5 text-muted-foreground">{post.bodyPreview}</p>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] tabular-nums text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            {formatScore(post.commentCount)}
          </span>
          <span className="hidden items-center gap-1 sm:inline-flex">
            <Eye className="h-3 w-3" />
            {formatScore(post.viewCount)}
          </span>
          <span onClick={(e) => e.stopPropagation()} className="ml-auto">
            <PostActionsMenu post={post} />
          </span>
        </div>
      </div>

      {post.imageUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={post.imageUrl}
          alt=""
          loading="lazy"
          className="hidden h-16 w-16 shrink-0 rounded-md border object-cover sm:block"
        />
      )}
    </div>
  );
}
